import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdVideoLibrary } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { fetchEducationalVideos } from "../store/reducers/videoSectionSlice";
import Player from "@vimeo/player";
import { toast } from "react-toastify";

import {
  submitVideoQuiz,
  resetVideoQuizState,
} from "../store/reducers/quizzesSlice";
import QuizAlertModal from "./components/QuizAlertModal";
import { setQuizAlertModal } from "../store/reducers/modalSlice";
import { toWords } from "number-to-words";

const EducationalVideos = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const { organizationDetails } = useSelector((state) => state.organization);
  const { educationalVideos, loading } = useSelector(
    (state) => state.eduVideos || {}
  );

  const { videoQuiz } = useSelector((state) => state.quizzes || {});
  const { quizAlertModal } = useSelector((state) => state.modal || {});
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(fetchEducationalVideos(organizationDetails.id));
    }
  }, [dispatch, organizationDetails?.id]);


  const getEmbedUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtube.com/watch")) {
      return url.replace("watch?v=", "embed/") + "?enablejsapi=1";
    }

    if (url.includes("vimeo.com")) {
      if (url.includes("player.vimeo.com")) return url;
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return url;
  };

  useEffect(() => {
    if (videoQuiz?.data) {
      toast.success("Quiz request submitted successfully!", {
        onClose: () => {
          dispatch(resetVideoQuizState());
        },
      });

      dispatch(setQuizAlertModal(false));
      setSelectedVideo(null);
    }
  }, [videoQuiz?.data, dispatch]);

  useEffect(() => {
    if (videoQuiz?.error) {
      toast.error(
        videoQuiz?.error?.error ||
          videoQuiz?.error?.message ||
          "Something went wrong"
      );
    }
  }, [videoQuiz?.error]);

  useEffect(() => {
    if (!educationalVideos?.length) return;

    const players = [];

    educationalVideos.forEach((item) => {
      ["dk", "en"].forEach((lang) => {
        const iframeId = `video-${item.id}-${lang}`;
        const iframe = document.getElementById(iframeId);

        if (!iframe) return;

        try {
          const player = new Player(iframe);

          player.on("ended", () => {
            setSelectedVideo(item);

            // ✅ OPEN MODAL USING REDUX
            dispatch(setQuizAlertModal(true));
          });

          players.push(player);
        } catch (err) {
          console.log("Player init error", err);
        }
      });
    });

    return () => {
      players.forEach((player) => {
        player.off("ended");
      });
    };
  }, [educationalVideos, dispatch]);

  /* ---------------- HANDLE API CALL ---------------- */
  const handleYes = async () => {
    if (!selectedVideo) return;

    const payload = {
      video_id: selectedVideo?.id,
      organization_id: organizationDetails?.id,
      want_video_quiz: true,
    };

    await dispatch(submitVideoQuiz(payload));
  };

  const handleNo = () => {
    setSelectedVideo(null);
    dispatch(setQuizAlertModal(false));
  };

  const num = organizationDetails?.videos_questions_per_quiz || 3;
  const number_of_quiz = toWords(num);

  /* ---------------- RENDER VIDEOS ---------------- */
  const renderVideos = (isDanish = false) => {
    return educationalVideos
      ?.filter((item) => item.active)
      ?.map((item) => {
        const title = isDanish ? item.name_dk : item.name;
        const link = isDanish ? item.link_dk : item.link;

        if (!link) return null;

        return (
          <div className="col-12 mt-3" key={`${item.id}-${isDanish}`}>
            <h5>{title}</h5>

            <div className="card shadow-sm">
              <div className="card-body">
                <iframe
                  id={`video-${item.id}-${isDanish ? "dk" : "en"}`}
                  src={getEmbedUrl(link)}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ width: "100%", height: "400px" }}
                  title={title}
                />
              </div>
            </div>

            {item.tag && (
              <h6 className="mt-2">
                <b>Tag:</b> {item.tag}
              </h6>
            )}

            <hr />
          </div>
        );
      });
  };

  return (
    <div className="container mt-2">
      {/* HEADER */}
      <div className="text-center">
        <h3>
          <MdVideoLibrary size={30} /> Educational Video
        </h3>
      </div>

      {/* TABS */}
      <ul className="nav nav-pills mb-3">
        <li className="nav-item">
          <button
            className="nav-link active btn-dark"
            data-bs-toggle="pill"
            data-bs-target="#danish"
            type="button"
          >
            {t("IntroductionVideo.label.Danish")}
          </button>
        </li>

        <li className="nav-item mx-3">
          <button
            className="nav-link btn-dark"
            data-bs-toggle="pill"
            data-bs-target="#english"
            type="button"
          >
            {t("IntroductionVideo.label.English")}
          </button>
        </li>
      </ul>

      <hr />

      {/* CONTENT */}
      <div className="tab-content">
        <div className="tab-pane fade show active" id="danish">
          <div className="row">
            {loading ? <p>Loading...</p> : renderVideos(true)}
          </div>
        </div>

        <div className="tab-pane fade" id="english">
          <div className="row">
            {loading ? <p>Loading...</p> : renderVideos(false)}
          </div>
        </div>
      </div>
      {quizAlertModal && (
        <QuizAlertModal
          description={`You have watched an educational video. Do you want to increase your score by answering ${number_of_quiz} questions?`}
          subText={`Questions will be sent after a ${organizationDetails?.delay_videos_quiz} hours.`}
          open={quizAlertModal}
          loading={videoQuiz?.loading}
          onClose={() => dispatch(setQuizAlertModal(false))}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </div>
  );
};

export default EducationalVideos;
